<script setup>
import SiteLayout from '@/Layouts/Layout.vue';
import SpfPolicyTestForm from '@/Pages/SpfPolicyTest/Partials/SpfPolicyTestForm.vue';
import Card from '@/Components/Card/Card.vue';
import SpfTree from '@/Pages/SpfPolicyTest/Partials/SpfTree.vue';
import ipRangeCheck from 'ip-range-check';
import StatusIcon from '@/Pages/Common/StatusIcon.vue';
import ACTION_TYPES from '@/Common/Enum/ActionType.js';
import Alert from '@/Components/Alert.vue';
import { TOOL_SPF_POLICY_TEST, captureToolUsed } from '@/Common/Posthog.js';

const props = defineProps({
  email: String,
  domain: String,
  ip: String,
  result: Object
});

const isIpInRange =
  ipRangeCheck(props.ip, props.result.ip4_ranges) ||
  ipRangeCheck(props.ip, props.result.ip6_ranges);

if (props.result?.spf_tree) {
  captureToolUsed({
    tool: TOOL_SPF_POLICY_TEST,
    properties: {
      tested_domain: props.domain,
      tested_ip: props.ip,
      ip_permitted: isIpInRange
    }
  });
}
</script>

<template>
  <SiteLayout
    :title="$t('spf_policy_test.show.spf_policy_test_tool')"
    :description="$t('spf_policy_test.show.description')"
  >
    <SpfPolicyTestForm :email="email" :ip="ip" />
    <Card v-if="result?.detail">
      <Alert variant="danger" :title="$t('explain.spf.record_not_found')">
        {{ result.detail }}
      </Alert>
    </Card>
    <Card>
      <div v-if="result?.spf_tree" class="flex flex-col gap-3">
        <div class="flex items-center justify-between md:justify-start">
          <div class="text-sm font-bold md:w-3/12">
            {{ $t('spf_policy_test.show.ip_is_permitted_sender') }}
          </div>
          <div class="text-sm">
            <StatusIcon
              :action-type="
                isIpInRange
                  ? ACTION_TYPES.NO_ACTION
                  : ACTION_TYPES.ACTION_REQUIRED
              "
            />
          </div>
        </div>

        <div class="flex items-center justify-between md:justify-start">
          <div class="text-sm font-bold md:w-3/12">
            {{ $t('spf_policy_test.show.spf_policy') }}
          </div>
          <div class="text-sm">
            <StatusIcon
              :action-type="
                result.record_status
                  ? ACTION_TYPES.NO_ACTION
                  : ACTION_TYPES.ACTION_REQUIRED
              "
            />
          </div>
        </div>

        <div class="flex items-center justify-between md:justify-start">
          <div class="text-sm font-bold md:w-3/12">IP</div>
          <div class="text-sm">{{ ip }}</div>
        </div>

        <div class="flex items-center justify-between md:justify-start">
          <div class="text-sm font-bold md:w-3/12">
            {{ $t('spf_policy_test.show.sender') }}
          </div>
          <div class="text-sm">{{ email }}</div>
        </div>

        <div class="flex items-center justify-between md:justify-start">
          <div class="text-sm font-bold md:w-3/12">
            {{ $t('spf_policy_test.show.domain') }}
          </div>
          <div class="text-sm">{{ domain }}</div>
        </div>
      </div>
      <Card v-if="result?.spf_tree" class="bg-white-500 mt-8">
        <div class="treeview js-treeview">
          <ul>
            <li>
              <SpfTree :node="result.spf_tree" :ip="ip" />
            </li>
          </ul>
        </div>
      </Card>
    </Card>
  </SiteLayout>
</template>
